import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {WorkSwipeNavigationDirective} from "src/app/modules/core/directives/work-swipe-navigation.directive";
import {WorkModalNavigationService} from "src/app/modules/core/services/work-modal-navigation.service";
import {ScreenSize} from "src/app/modules/shared/interfaces/ScreenSize.interface";
import {
  WorkModalNavigationDirection,
  WorkModalNavigationState,
} from "src/app/modules/shared/interfaces/work-modal-navigation.interface";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {WORK_DETAILS_SLIDE_ANIMATION_MS} from "src/app/modules/shared/constants/work-details.constants";

const PROGRESS_PANEL_CLASS_NAME = "work-details-progress-panel";
const PROGRESS_PANEL_ANIMATION_CLASS_NAME =
  "animate-work-details-progress-enter";
const TRANSITIONING_CLASS_NAME = "work-details-navigation-transitioning";

@Component({
  selector: "app-work-details-navigation",
  standalone: true,
  imports: [WorkSwipeNavigationDirective, TranslateModule],
  templateUrl: "./work-details-navigation.component.html",
  styleUrl: "./work-details-navigation.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkDetailsNavigationComponent implements OnInit, OnDestroy {
  public isMobile = false;
  public hasNavigation = false;
  public canPrevious = false;
  public canNext = false;
  public shouldDisplaySwipeTip = false;
  public readonly slideAnimationDuration = `${WORK_DETAILS_SLIDE_ANIMATION_MS}ms`;
  @ViewChild("slideContainer")
  public slideContainer!: ElementRef<HTMLElement>;

  private isTransitioning = false;
  private readonly subscriptions: Subscription[] = [];
  private slideTimeout?: ReturnType<typeof setTimeout>;
  private firstAnimationFrame?: number;
  private leavingClone?: HTMLElement;
  private readonly leavingProgressPanels: HTMLElement[] = [];
  private readonly pinnedProgressPanelStyles = new Map<
    HTMLElement,
    string | null
  >();
  private destroyed = false;

  constructor(
    private readonly navigationService: WorkModalNavigationService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public get isSwipeNavigationEnabled(): boolean {
    return this.isMobile && this.hasNavigation && !this.isTransitioning;
  }

  public readonly prepareSwipeNavigation = (
    direction: WorkModalNavigationDirection
  ): Promise<boolean> => this.prepareSwipeTransition(direction);

  ngOnInit(): void {
    const screenSizeSubscription = this.screenSizeService.screenSize$.subscribe(
      (screenSize: ScreenSize) => {
        this.isMobile = screenSize.isMobile;
        this.updateSwipeTipVisibility();
        this.cdr.markForCheck();
      }
    );
    const stateSubscription = this.navigationService.state$.subscribe(
      (state: WorkModalNavigationState | null) => {
        this.hasNavigation = state !== null;
        this.canPrevious = state?.canPrevious ?? false;
        this.canNext = state?.canNext ?? false;
        this.updateSwipeTipVisibility();
        this.cdr.markForCheck();
      }
    );
    const navigationSubscription =
      this.navigationService.navigationRequests$.subscribe((direction) =>
        this.performTransition(direction)
      );
    this.subscriptions.push(
      screenSizeSubscription,
      stateSubscription,
      navigationSubscription
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.cancelScheduledTransition();
    this.removeLeavingClone();
    this.restorePinnedProgressPanels();
    this.resetTransitionStyles();
    this.isTransitioning = false;
    this.navigationService.isTransitioning = false;
  }

  public dismissSwipeTip(): void {
    this.navigationService.markSwipeTipSeen();
    this.shouldDisplaySwipeTip = false;
    this.cdr.markForCheck();
  }

  public onTipKeyUp(event: KeyboardEvent): void {
    if (event.key === "Enter" || event.key === " ") {
      this.dismissSwipeTip();
    }
  }

  public onSwipeNavigation(direction: WorkModalNavigationDirection): void {
    if (!this.isTransitioning) {
      return;
    }

    const host = this.slideContainer.nativeElement;
    this.runGluedTransition(direction, this.readCurrentTranslateX(host));
  }

  public pinProgressPanelsForSwipe(): void {
    const host = this.slideContainer.nativeElement;
    const hostRect = host.getBoundingClientRect();
    this.pinnedProgressPanelStyles.clear();
    const candidates = Array.from(
      host.getElementsByClassName(PROGRESS_PANEL_CLASS_NAME)
    );
    for (const candidate of candidates) {
      if (
        !(candidate instanceof HTMLElement) ||
        getComputedStyle(candidate).position !== "fixed"
      ) {
        continue;
      }

      this.pinnedProgressPanelStyles.set(
        candidate,
        candidate.getAttribute("style")
      );
      this.pinElementAtCurrentPosition(candidate, candidate, hostRect);
    }
  }

  public restorePinnedProgressPanels(): void {
    for (const [panel, style] of this.pinnedProgressPanelStyles) {
      if (!panel.isConnected) {
        continue;
      }
      if (style === null) {
        panel.removeAttribute("style");
      } else {
        panel.setAttribute("style", style);
      }
    }
    this.pinnedProgressPanelStyles.clear();
  }

  private updateSwipeTipVisibility(): void {
    this.shouldDisplaySwipeTip =
      this.isMobile &&
      this.hasNavigation &&
      !this.navigationService.hasSeenSwipeTip();
  }

  private performTransition(direction: WorkModalNavigationDirection): void {
    if (this.isTransitioning) {
      return;
    }

    this.beginTransition();
    this.runGluedTransition(direction, 0);
  }

  private async prepareSwipeTransition(
    direction: WorkModalNavigationDirection
  ): Promise<boolean> {
    if (this.isTransitioning) {
      return false;
    }

    this.beginTransition();
    const prepared = await this.navigationService.prepareNavigation(direction);
    if (this.destroyed) {
      return false;
    }
    if (!prepared) {
      this.finishTransition();
    }
    return prepared;
  }

  private beginTransition(): void {
    this.isTransitioning = true;
    this.navigationService.isTransitioning = true;
  }

  // The leaving content keeps sliding as an inert clone while the new work enters glued one
  // width behind it, so no blank container ever crosses the screen.
  private runGluedTransition(
    direction: WorkModalNavigationDirection,
    startX: number
  ): void {
    const host = this.slideContainer.nativeElement;
    const width = host.offsetWidth;
    const container = this.elementRef.nativeElement.closest(".modal-container");
    const scrollTop = container?.scrollTop ?? 0;
    this.removeLeavingClone();
    this.leavingClone = this.createLeavingClone(host, startX, scrollTop);
    host.classList.add(TRANSITIONING_CLASS_NAME);
    this.navigationService.applyNavigation(direction);
    container?.scrollTo({top: 0});
    const entryOffset = direction === "next" ? width : -width;
    host.style.transition = "none";
    host.style.transform = `translateX(${startX + entryOffset}px)`;
    // FLIP: committing the start positions with a forced reflow lets the slide begin on the
    // very first frame painted after the swap, without waiting for animation frames.
    host.getBoundingClientRect();
    this.startGluedSlide(host, entryOffset, scrollTop);
  }

  private createLeavingClone(
    host: HTMLElement,
    startX: number,
    scrollTop: number
  ): HTMLElement {
    const clone = host.cloneNode(true) as HTMLElement;
    clone.setAttribute("aria-hidden", "true");
    clone.style.position = "absolute";
    clone.style.top = "0";
    clone.style.left = "0";
    clone.style.width = "100%";
    clone.style.zIndex = "1";
    clone.style.pointerEvents = "none";
    clone.style.transition = "none";
    // -scrollTop keeps the previously visible area in place once the container scroll resets.
    clone.style.transform = `translate(${startX}px, ${-scrollTop}px)`;
    this.pinFixedDescendants(host, clone);
    this.disableProgressPanelAnimations(clone);
    host.parentElement?.insertBefore(clone, host);
    this.extractLeavingProgressPanels(clone, host, startX, scrollTop);
    return clone;
  }

  // position:fixed descendants (e.g. the details progress panel) would resolve against the
  // transformed clone instead of the viewport and land out of sight: pin them where they were
  // visible so they slide away together with the leaving content.
  private pinFixedDescendants(host: HTMLElement, clone: HTMLElement): void {
    const hostRect = host.getBoundingClientRect();
    const hostCandidates = host.getElementsByClassName("fixed");
    const cloneCandidates = clone.getElementsByClassName("fixed");
    for (let index = 0; index < hostCandidates.length; index++) {
      const source = hostCandidates[index];
      const target = cloneCandidates[index];
      if (
        !(source instanceof HTMLElement) ||
        !(target instanceof HTMLElement) ||
        getComputedStyle(source).position !== "fixed"
      ) {
        continue;
      }

      this.pinElementAtCurrentPosition(source, target, hostRect);
    }
  }

  private disableProgressPanelAnimations(clone: HTMLElement): void {
    for (const candidate of Array.from(
      clone.getElementsByClassName(PROGRESS_PANEL_ANIMATION_CLASS_NAME)
    )) {
      if (candidate instanceof HTMLElement) {
        candidate.style.animation = "none";
      }
    }
  }

  private extractLeavingProgressPanels(
    clone: HTMLElement,
    host: HTMLElement,
    startX: number,
    scrollTop: number
  ): void {
    const parent = host.parentElement;
    if (!parent) {
      return;
    }

    const panels = Array.from(
      clone.getElementsByClassName(PROGRESS_PANEL_CLASS_NAME)
    );
    for (const candidate of panels) {
      if (!(candidate instanceof HTMLElement)) {
        continue;
      }

      candidate.setAttribute("inert", "");
      candidate.style.zIndex = "2";
      candidate.style.pointerEvents = "none";
      candidate.style.transition = "none";
      candidate.style.transform = `translate(${startX}px, ${-scrollTop}px)`;
      parent.insertBefore(candidate, host);
      this.leavingProgressPanels.push(candidate);
    }
  }

  private pinElementAtCurrentPosition(
    source: HTMLElement,
    target: HTMLElement,
    hostRect: DOMRect
  ): void {
    const sourceRect = source.getBoundingClientRect();
    target.style.position = "absolute";
    target.style.top = `${sourceRect.top - hostRect.top}px`;
    target.style.left = `${sourceRect.left - hostRect.left}px`;
    target.style.width = `${sourceRect.width}px`;
    target.style.height = `${sourceRect.height}px`;
    target.style.bottom = "auto";
    target.style.right = "auto";
    target.style.transform = "none";
    target.style.setProperty("translate", "none");
  }

  private startGluedSlide(
    host: HTMLElement,
    entryOffset: number,
    scrollTop: number
  ): void {
    const duration = WORK_DETAILS_SLIDE_ANIMATION_MS;
    const clone = this.leavingClone;
    if (clone) {
      clone.style.transition = `transform ${duration}ms ease-out`;
      clone.style.transform = `translate(${-entryOffset}px, ${-scrollTop}px)`;
    }
    for (const panel of this.leavingProgressPanels) {
      panel.style.transition = `transform ${duration}ms ease-out`;
      panel.style.transform = `translate(${-entryOffset}px, ${-scrollTop}px)`;
    }
    host.style.transition = `transform ${duration}ms ease-out`;
    host.style.transform = "translateX(0)";
    // The cleanup timer is anchored to the first painted frame (where the transition really
    // starts): a timer counted from the click could cut the slide short on a slow swap render.
    this.firstAnimationFrame = requestAnimationFrame(() => {
      this.firstAnimationFrame = undefined;
      this.slideTimeout = setTimeout(() => {
        this.slideTimeout = undefined;
        this.finishTransition();
      }, duration);
    });
  }

  private readCurrentTranslateX(host: HTMLElement): number {
    const transform = getComputedStyle(host).transform;
    if (!transform || transform === "none") {
      return 0;
    }
    return new DOMMatrixReadOnly(transform).m41;
  }

  private removeLeavingClone(): void {
    this.leavingClone?.remove();
    this.leavingClone = undefined;
    for (const panel of this.leavingProgressPanels) {
      panel.remove();
    }
    this.leavingProgressPanels.length = 0;
  }

  private finishTransition(): void {
    this.removeLeavingClone();
    this.resetTransitionStyles();
    this.restorePinnedProgressPanels();
    this.restartProgressPanelAnimation();
    this.isTransitioning = false;
    this.navigationService.isTransitioning = false;
    this.cdr.markForCheck();
  }

  private resetTransitionStyles(): void {
    const host = this.slideContainer?.nativeElement;
    if (!host) {
      return;
    }
    host.style.transition = "";
    host.style.transform = "";
    host.style.opacity = "";
    host.classList.remove(TRANSITIONING_CLASS_NAME);
  }

  private restartProgressPanelAnimation(): void {
    const host = this.slideContainer.nativeElement;
    const panels = Array.from(
      host.getElementsByClassName(PROGRESS_PANEL_ANIMATION_CLASS_NAME)
    ).filter((panel): panel is HTMLElement => panel instanceof HTMLElement);
    if (panels.length === 0) {
      return;
    }

    for (const panel of panels) {
      panel.style.animation = "none";
    }
    host.getBoundingClientRect();
    for (const panel of panels) {
      panel.style.animation = "";
    }
  }

  private cancelScheduledTransition(): void {
    if (this.slideTimeout !== undefined) {
      clearTimeout(this.slideTimeout);
    }
    if (this.firstAnimationFrame !== undefined) {
      cancelAnimationFrame(this.firstAnimationFrame);
    }
  }
}
